import React, { useEffect, useState, useRef } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocaleDisplayName, type Locale } from '../utils/language';
import { useSafeT } from '../hooks/useSafeT';
const SUPPORTED_LOCALES: Locale[] = ['en', 'zh', 'ms', 'ta', 'th'];
export function LanguageSelector() {
  const {
    language,
    changeLanguage
  } = useLanguage();
  const t = useSafeT();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);
  const handleLanguageChange = (locale: Locale) => {
    changeLanguage(locale);
    setIsOpen(false);
  };
  return <div ref={dropdownRef} className="relative" data-id="element-897">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-grey-400/10 transition-colors duration-200" aria-label={t('aria.languageSelector', {
      defaultValue: 'Language selector'
    })} aria-expanded={isOpen} aria-haspopup="true" data-id="element-898">
        <Globe className="h-4 w-4 text-grey-600" data-id="element-899" />
        <span className="text-sm text-grey-600" data-id="element-900">
          {getLocaleDisplayName(language)}
        </span>
      </button>

      {isOpen && <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-grey-400/20 py-2 z-50" role="menu" aria-orientation="vertical" aria-labelledby="language-menu" data-id="element-901">
          {SUPPORTED_LOCALES.map(locale => <button key={locale} onClick={() => handleLanguageChange(locale)} className={`w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${language === locale ? 'bg-sage-mint/10 text-sage-mint font-medium' : 'text-grey-600 hover:bg-grey-400/5'}`} role="menuitem" aria-pressed={language === locale} data-id="element-902">
              <div className="flex items-center justify-between" data-id="element-903">
                <span data-id="element-904">{getLocaleDisplayName(locale)}</span>
                {language === locale && <span className="text-sage-mint" data-id="element-905">✓</span>}
              </div>
            </button>)}
        </div>}
    </div>;
}
export default LanguageSelector;